import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { AGENT_STATUS, AGENT_STATUS_COLORS, AGENT_STATUS_ON_CALL, TRANSFER_PANEL_TITLE } from './pbxConstants';

const useStyles = makeStyles(theme => ({
  panel: {
    width: '260px',
    flexShrink: 0,
    maxHeight: 'calc(100vh - 40px)',
    marginRight: theme.spacing(2),
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '12px',
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(0.5),
  },
  title: {
    fontSize: '13px',
    fontWeight: 700,
    color: theme.palette.text.primary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  closeBtn: {
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: '16px',
    lineHeight: 1,
    color: theme.palette.text.secondary,
    padding: '2px 4px',
    borderRadius: '4px',
    '&:hover': {
      background: theme.palette.action.hover,
    },
  },
  subtitle: {
    fontSize: '12px',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.5),
  },
  emptyText: {
    fontSize: '12px',
    color: theme.palette.text.secondary,
    textAlign: 'center',
    padding: theme.spacing(2),
  },
  agentRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: '8px 10px',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'background 0.15s ease',
    '&:hover': {
      background: theme.palette.action.hover,
    },
  },
  agentRowDisabled: {
    cursor: 'not-allowed',
    opacity: 0.5,
    '&:hover': {
      background: 'transparent',
    },
  },
  avatar: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    background: theme.palette.grey[300],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: 600,
    color: theme.palette.text.primary,
    flexShrink: 0,
    position: 'relative',
  },
  statusDot: {
    width: '9px',
    height: '9px',
    borderRadius: '50%',
    border: `2px solid ${theme.palette.background.paper}`,
    position: 'absolute',
    bottom: '-2px',
    right: '-2px',
  },
  name: {
    fontSize: '13px',
    fontWeight: 500,
    color: theme.palette.text.primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  ext: {
    fontSize: '11px',
    color: theme.palette.text.secondary,
  },
}));

const STATUS_LABEL = {
  [AGENT_STATUS.AVAILABLE]: 'Available',
  [AGENT_STATUS.BREAK]: 'On break',
  [AGENT_STATUS.OFFLINE]: 'Offline',
  [AGENT_STATUS_ON_CALL]: 'On a call',
};

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

function PBXTransferAgentsPanel({
  agents,
  loading,
  transferring,
  onTransfer,
  title,
  actionHint,
  actionVerb,
  onClose,
  // Which presence status makes a row clickable — plain transfer only makes
  // sense against a free agent (AVAILABLE); Monitor/Coaching/Conference only
  // make sense against an agent who is actually mid-call (ON_CALL).
  selectableStatus,
  emptyText,
}) {
  const classes = useStyles();
  const list = agents || [];
  const verb = actionVerb || 'Transfer to';
  const wantedStatus = selectableStatus || AGENT_STATUS.AVAILABLE;

  return (
    <div className={classes.panel}>
      <div className={classes.titleRow}>
        <div className={classes.title}>{title || TRANSFER_PANEL_TITLE}</div>
        {onClose && (
          <button type="button" className={classes.closeBtn} onClick={onClose} title="Close">
            ×
          </button>
        )}
      </div>
      <div className={classes.subtitle}>
        {transferring
          ? 'Transferring…'
          : loading && list.length === 0
            ? 'Loading agents…'
            : actionHint || 'Select an available agent to transfer to'}
      </div>

      {list.length === 0 && !loading && (
        <div className={classes.emptyText}>{emptyText || 'No other agents found.'}</div>
      )}

      {list.map((agent) => {
        const isAvailable = agent.status === wantedStatus && !transferring;
        return (
          <div
            key={agent.userId || agent.extension}
            className={`${classes.agentRow} ${!isAvailable ? classes.agentRowDisabled : ''}`}
            onClick={() => isAvailable && onTransfer(agent)}
            title={
              isAvailable
                ? `${verb} ${agent.displayName}`
                : transferring
                  ? 'A transfer is already in progress'
                  : `${agent.displayName} is ${STATUS_LABEL[agent.status] || 'unavailable'}`
            }
          >
            <div className={classes.avatar}>
              {getInitials(agent.displayName)}
              <span
                className={classes.statusDot}
                style={{ background: AGENT_STATUS_COLORS[agent.status] || AGENT_STATUS_COLORS[AGENT_STATUS.OFFLINE] }}
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className={classes.name}>{agent.displayName}</div>
              <div className={classes.ext}>Ext. {agent.extension}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default PBXTransferAgentsPanel;
